export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_IMAGE_PIXELS = 40_000_000;
export const MAX_IMAGE_DIMENSION = 20_000;

export interface InputValidationLimits {
  maxFileBytes: number;
  maxImagePixels: number;
  maxImageDimension: number;
}

export const DEFAULT_INPUT_LIMITS: Readonly<InputValidationLimits> = Object.freeze({
  maxFileBytes: MAX_FILE_BYTES,
  maxImagePixels: MAX_IMAGE_PIXELS,
  maxImageDimension: MAX_IMAGE_DIMENSION,
});

export type InputErrorCode =
  | 'EMPTY_FILE'
  | 'FILE_TOO_LARGE'
  | 'UNSUPPORTED_FORMAT'
  | 'EXTENSION_MISMATCH'
  | 'MIME_MISMATCH'
  | 'MALFORMED_IMAGE'
  | 'IMAGE_DIMENSIONS_EXCEEDED';

export class InputValidationError extends Error {
  constructor(readonly code: InputErrorCode, message: string) {
    super(message);
    this.name = 'InputValidationError';
  }
}

export interface InputDescriptor {
  filename: string;
  size: number;
  declaredMime: string;
  header: Uint8Array;
}

export interface ValidatedInput {
  detectedMime: 'image/jpeg' | 'image/png';
  format: 'JPEG' | 'PNG';
  width: number;
  height: number;
}

export function validateInputDescriptor(
  input: InputDescriptor,
  limits: Readonly<InputValidationLimits> = DEFAULT_INPUT_LIMITS,
): ValidatedInput {
  if (input.size === 0) throw new InputValidationError('EMPTY_FILE', 'The selected file is empty.');
  if (input.size > limits.maxFileBytes) {
    throw new InputValidationError('FILE_TOO_LARGE', `The image exceeds the ${formatMegabytes(limits.maxFileBytes)} MB processing limit.`);
  }

  const detectedMime = detectMime(input.header);
  if (!detectedMime) {
    throw new InputValidationError('UNSUPPORTED_FORMAT', 'Only JPEG and PNG images are supported in this release.');
  }

  const extension = input.filename.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  const allowedExtensions = detectedMime === 'image/jpeg' ? ['jpg', 'jpeg'] : ['png'];
  if (!extension || !allowedExtensions.includes(extension)) {
    throw new InputValidationError('EXTENSION_MISMATCH', 'The filename extension does not match the image content.');
  }
  if (input.declaredMime && input.declaredMime !== detectedMime) {
    throw new InputValidationError('MIME_MISMATCH', 'The declared MIME type does not match the image content.');
  }

  const dimensions = detectedMime === 'image/png' ? readPngDimensions(input.header) : readJpegDimensions(input.header);
  if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) {
    throw new InputValidationError('MALFORMED_IMAGE', 'The image header is malformed or incomplete.');
  }
  if (
    dimensions.width > limits.maxImageDimension ||
    dimensions.height > limits.maxImageDimension ||
    dimensions.width * dimensions.height > limits.maxImagePixels
  ) {
    throw new InputValidationError('IMAGE_DIMENSIONS_EXCEEDED', 'The decoded image dimensions exceed the safe processing limit.');
  }

  return {
    detectedMime,
    format: detectedMime === 'image/png' ? 'PNG' : 'JPEG',
    ...dimensions,
  };
}

export function sanitizeArtifactFilename(filename: string): string {
  const leaf = filename.replaceAll('\\', '/').split('/').at(-1) ?? '';
  const clean = Array.from(leaf).filter((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code >= 32 && code !== 127;
  }).join('').trim().slice(0, 255);
  return clean || 'artifact';
}

function detectMime(bytes: Uint8Array): ValidatedInput['detectedMime'] | undefined {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 &&
    bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
  ) return 'image/png';
  return undefined;
}

function readPngDimensions(bytes: Uint8Array): { width: number; height: number } | undefined {
  if (bytes.length < 24 || String.fromCharCode(...bytes.slice(12, 16)) !== 'IHDR') return undefined;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return { width: view.getUint32(16), height: view.getUint32(20) };
}

function readJpegDimensions(bytes: Uint8Array): { width: number; height: number } | undefined {
  const startOfFrame = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
  let offset = 2;
  while (offset + 9 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    while (bytes[offset] === 0xff) offset += 1;
    const marker = bytes[offset];
    offset += 1;
    if (marker === undefined || marker === 0xda || marker === 0xd9) break;
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
    if (offset + 2 > bytes.length) break;
    const segmentLength = (bytes[offset] ?? 0) * 256 + (bytes[offset + 1] ?? 0);
    if (segmentLength < 2 || offset + segmentLength > bytes.length) break;
    if (startOfFrame.has(marker) && segmentLength >= 7) {
      return {
        height: (bytes[offset + 3] ?? 0) * 256 + (bytes[offset + 4] ?? 0),
        width: (bytes[offset + 5] ?? 0) * 256 + (bytes[offset + 6] ?? 0),
      };
    }
    offset += segmentLength;
  }
  return undefined;
}

function formatMegabytes(bytes: number): string {
  return Number.isInteger(bytes / 1024 / 1024)
    ? String(bytes / 1024 / 1024)
    : (bytes / 1024 / 1024).toFixed(1);
}
